import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  Users, 
  Camera, 
  AlertTriangle,
  Flame,
  Upload
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { apiService } from '../services/apiService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { formatUserDateTime, formatSystemDateTime } from '../utils/dateUtils';

// Custom fire icon for map markers
const fireIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#E85D04" width="40" height="40">
      <path d="M12 2C13.1 2 14 2.9 14 4C14 5.3 13.1 6.1 12.5 6.5C12.2 6.7 12 7 12 7.3V8H13C14.1 8 15 8.9 15 10V11C15 12.1 14.1 13 13 13H11C9.9 13 9 12.1 9 11V10C9 8.9 9.9 8 11 8H12V7.3C12 6.5 12.4 5.8 13 5.3C13.6 4.8 14 4.1 14 3.3C14 2.6 13.4 2 12.7 2H12Z"/>
      <path d="M8.5 14.5C8.5 16.4 10.1 18 12 18S15.5 16.4 15.5 14.5C15.5 13.1 14.6 11.9 13.3 11.3C12.9 11.1 12.5 11 12 11S11.1 11.1 10.7 11.3C9.4 11.9 8.5 13.1 8.5 14.5Z"/>
    </svg>
  `),
  iconSize: [48, 48],
  iconAnchor: [24, 48],
  popupAnchor: [0, -48],
});

// Custom camera icon for photo markers
const cameraIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1B4D3E" width="20" height="20">
      <path d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12S13.933 8.5 12 8.5 8.5 10.067 8.5 12 10.067 15.5 12 15.5Z"/>
      <path d="M20 4H16.83L15 2H9L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM12 17C9.24 17 7 14.76 7 12S9.24 7 12 7 17 9.24 17 12 14.76 17 12 17Z"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

const FireDetailPage = () => {
  const { fireId } = useParams();
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Fetch fire details
  const { data: fireData, isLoading: fireLoading } = useQuery(
    ['fire', fireId],
    () => apiService.getFire(fireId)
  );

  // Fetch photos for this fire
  const { data: photosData, isLoading: photosLoading } = useQuery(
    ['fire-photos', fireId],
    () => apiService.getPhotos({ fire_id: fireId })
  );

  // Fetch fogos.pt photos for this fire (via BFF — server-side key)
  const { data: fogosPhotosData, isLoading: fogosPhotosLoading, error: fogosPhotosError } = useQuery(
    ['fire-fogos-photos', fireId],
    () => apiService.getFogosPhotos(fireId, { per_page: 100 }),
    { retry: false }
  );

  const fire = fireData?.fire;
  const photos = photosData?.photos || [];
  const fogosPhotos = fogosPhotosData?.data || [];

  const getStatusBadge = (fire) => {
    if (fire?.is_important) {
      return (
        <span className="status-badge status-important">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Importante
        </span>
      );
    }
    if (fire?.is_active) {
      return <span className="status-badge status-active">Ativo</span>;
    }
    return <span className="status-badge status-inactive">Inativo</span>;
  };

  const PhotoModal = ({ photo, onClose }) => {
    if (!photo) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl max-h-full overflow-auto">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Detalhes da Foto
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <img
                  src={apiService.getPhotoUrl(photo.id)}
                  alt={`Foto de ${fire?.name}`}
                  className="w-full rounded-lg"
                />
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Informações
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{photo.user?.name} {photo.user?.surname}</span>
                      {photo.user?.entity && (
                        <span className="text-gray-500 dark:text-gray-400">({photo.user.entity})</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        Tirada em: {formatUserDateTime(photo.taken_at)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Camera className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        Enviada em: {formatSystemDateTime(photo.uploaded_at)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {photo.description && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      Descrição
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {photo.description}
                    </p>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Coordenadas
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {photo.lat.toFixed(6)}, {photo.lng.toFixed(6)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (fireLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!fire) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Incêndio não encontrado
        </h2>
        <Link to="/fires" className="btn-primary">
          Voltar à lista
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/fires"
            className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {fire.location}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {fire.district} • {fire.concelho}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {getStatusBadge(fire)}
          <Link
            to={`/upload?fire_id=${fire.id}`}
            className="btn-fire flex items-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>Enviar Foto</span>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fire Information */}
        <div className="lg:col-span-1 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Informações do Incêndio
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Flame className="w-5 h-5 text-fire-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Natureza</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {fire.natureza || 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Início</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {fire.date_str} às {fire.hour_str}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Localização</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {fire.freguesia || fire.concelho}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Camera className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Fotos</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {photos.length} fotos
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resources */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Meios Empenhados
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {fire.personnel}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Operacionais
                </div>
              </div>
              
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {fire.terrain_vehicles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Veículos
                </div>
              </div>
              
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {fire.aerial_means}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Aéreos
                </div>
              </div>
              
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                  {fire.water_means}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Aquáticos
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map and Photos */}
        <div className="lg:col-span-2 space-y-6">
          {/* Map */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Localização e Fotos
            </h2>
            
            <div className="h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <MapContainer
                center={[fire.lat, fire.lng]}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                
                {/* Fire location */}
                <Marker position={[fire.lat, fire.lng]} icon={fireIcon}>
                  <Popup>
                    <div className="text-center">
                      <p className="font-medium">{fire.location}</p>
                      <p className="text-sm text-gray-600">Localização do incêndio</p>
                    </div>
                  </Popup>
                </Marker>
                
                {/* Photo locations */}
                {photos.map((photo) => (
                  <Marker key={photo.id} position={[photo.lat, photo.lng]} icon={cameraIcon}>
                    <Popup>
                      <div className="text-center">
                        <img
                          src={apiService.getPhotoUrl(photo.id)}
                          alt="Foto"
                          className="w-32 h-24 object-cover rounded mb-2 cursor-pointer"
                          onClick={() => setSelectedPhoto(photo)}
                        />
                        <p className="text-sm">
                          Por {photo.user?.name} {photo.user?.surname}
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          {/* Photos Gallery */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Galeria de Fotos ({photos.length})
              </h2>
              <Link
                to={`/photos?fire_id=${fire.id}`}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 text-sm font-medium"
              >
                Ver todas
              </Link>
            </div>
            
            {photosLoading ? (
              <LoadingSpinner />
            ) : photos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {photos.slice(0, 8).map((photo) => (
                  <div
                    key={photo.id}
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    <img
                      src={apiService.getPhotoUrl(photo.id)}
                      alt={`Foto por ${photo.user?.name}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                      <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute bottom-1 left-1 right-1">
                      <p className="text-xs text-white bg-black bg-opacity-50 rounded px-1 py-0.5 truncate">
                        {photo.user?.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Ainda não há fotos para este incêndio
                </p>
                <Link
                  to={`/upload?fire_id=${fire.id}`}
                  className="btn-primary"
                >
                  Enviar primeira foto
                </Link>
              </div>
            )}
          </div>

          {/* Fogos.pt photos */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Fotos do fogos.pt ({fogosPhotos.length})
              </h2>
              {fogosPhotosData?.meta?.total > fogosPhotos.length && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Total: {fogosPhotosData.meta.total}
                </span>
              )}
            </div>

            {fogosPhotosLoading ? (
              <LoadingSpinner />
            ) : fogosPhotosError ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Não foi possível obter fotos do fogos.pt.
              </p>
            ) : fogosPhotos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {fogosPhotos.map((p) => {
                  const statusColor =
                    p.status === 'approved'
                      ? 'bg-green-600'
                      : p.status === 'rejected'
                      ? 'bg-red-600'
                      : 'bg-yellow-600';
                  return (
                    <a
                      key={p.id}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative group block"
                    >
                      <img
                        src={p.url}
                        alt={p.signature || 'foto fogos.pt'}
                        loading="lazy"
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <div className="absolute top-1 left-1 flex gap-1">
                        <span className={`text-[10px] text-white ${statusColor} rounded px-1 py-0.5`}>
                          {p.status}
                        </span>
                        {p.public === false && (
                          <span className="text-[10px] text-white bg-gray-700 rounded px-1 py-0.5">
                            privada
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-1 left-1 right-1">
                        <p className="text-xs text-white bg-black bg-opacity-50 rounded px-1 py-0.5 truncate">
                          {p.signature || 'Anónimo'}
                          {p.taken_at && ` • ${formatUserDateTime(p.taken_at)}`}
                        </p>
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Sem fotos no fogos.pt para este incêndio.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Photo Modal */}
      <PhotoModal
        photo={selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
      />
    </div>
  );
};

export default FireDetailPage;
